import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import useCallbackRef from './useCallbackRef';
import uniqid from 'uniqid';

type CSSVariables<T extends HTMLElement> = {
  target?: T;
  stylesObject: object;
};

export default function useCSSVariables<T extends HTMLElement>(
  props: CSSVariables<T>,
) {
  const [styles, stylesRef] = useCallbackRef<HTMLStyleElement>(null);
  const stylesId = useRef(uniqid());

  const { target = document.body, stylesObject } = props;

  const styleString = useMemo(
    () => `[data-styles-id="${stylesId.current}"]{
    ${Object.entries(stylesObject).reduce(
      (stylesString, item) => `${stylesString} --${item[0]}: ${item[1]};`,
      '',
    )}
  }`,
    [stylesObject],
  );

  useEffect(() => {
    if (!styles || !target) return;
    target.setAttribute(`data-styles-id`, stylesId.current);

    return () => {
      target.removeAttribute('data-styles-id');
    };
  }, [styles, target]);

  const cssStyles = useMemo(() => {
    return <style ref={stylesRef}> {styleString} </style>;
  }, [styleString, stylesRef]);

  return createPortal(cssStyles, document.head);
}
